import { createTheme, type ThemeOptions } from '@mui/material/styles';

// Declare module augmentation for custom palette colors
declare module '@mui/material/styles' {
  interface Palette {
    app: {
      ownerTypes: {
        mine: { main: string; background: string };
        owned: { main: string; background: string };
        house: { main: string; background: string };
        competition: { main: string; background: string };
        new: { main: string; background: string };
      };
      activity: {
        live: { main: string; background: string };
        active: { main: string; background: string };
        new: { main: string; background: string };
        inactive1: { main: string; background: string };
        inactive2: { main: string; background: string };
        inactive3: { main: string; background: string };
        inactive: { main: string; background: string };
      };
      competition: {
        headout: { main: string; background: string };
        fever: { main: string; background: string };
      };
      map: {
        userLocationActual: string;
        userLocationDefault: string;
        userLocationPulseActual: string;
        userLocationPulseDefault: string;
        dealMarkerPulsation: string;
      };
      google: {
        border: string;
        text: string;
        background: string;
        hoverBackground: string;
        loadingColor: string;
        disabledText: string;
        blue: string;
        green: string;
        yellow: string;
        red: string;
      };
      salesforce: {
        blue: string;
      };
      gradients: {
        coffeeLight: string;
        coffeeDark: string;
      };
    };
  }

  interface PaletteOptions {
    app?: {
      ownerTypes?: {
        mine?: { main: string; background: string };
        owned?: { main: string; background: string };
        house?: { main: string; background: string };
        competition?: { main: string; background: string };
        new?: { main: string; background: string };
      };
      activity?: {
        live?: { main: string; background: string };
        active?: { main: string; background: string };
        new?: { main: string; background: string };
        inactive1?: { main: string; background: string };
        inactive2?: { main: string; background: string };
        inactive3?: { main: string; background: string };
        inactive?: { main: string; background: string };
      };
      competition?: {
        headout?: { main: string; background: string };
        fever?: { main: string; background: string };
      };
      map?: {
        userLocationActual?: string;
        userLocationDefault?: string;
        userLocationPulseActual?: string;
        userLocationPulseDefault?: string;
        dealMarkerPulsation?: string;
      };
      google?: {
        border?: string;
        text?: string;
        background?: string;
        hoverBackground?: string;
        loadingColor?: string;
        disabledText?: string;
        blue?: string;
        green?: string;
        yellow?: string;
        red?: string;
      };
      salesforce?: {
        blue?: string;
      };
      gradients?: {
        coffeeLight?: string;
        coffeeDark?: string;
      };
    };
  }
}

// Declare custom variants for Button
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    pill: true;
  }
}

// Coffee color palette - enhanced with all app colors
const coffeeColors = {
  primary: {
    main: '#92400e', // Coffee brown
    light: '#a05426',
    dark: '#7c350c',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#d97706', // Amber
    light: '#dd851f',
    dark: '#bf6805',
    contrastText: '#ffffff',
  },
  success: {
    main: '#059669',
    light: '#1ea57a',
    dark: '#04845c',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#eab308',
    light: '#ecc121',
    dark: '#ce9e07',
    contrastText: '#000000',
  },
  error: {
    main: '#dc2626',
    light: '#e03c3c',
    dark: '#c22222',
    contrastText: '#ffffff',
  },
  // Custom app colors
  app: {
    // Owner types no longer use colors - they control shape instead
    // Keeping this for backwards compatibility if needed
    ownerTypes: {
      mine: {
        main: '#00A32A',
        background: '#00A32A',
      },
      owned: {
        main: '#CC0000',
        background: '#CC0000',
      },
      house: {
        main: '#DD7700',
        background: '#DD7700',
      },
      competition: {
        main: '#3b3bb6',
        background: '#3b3bb6',
      },
      new: {
        main: '#24313f',
        background: '#24313f',
      },
    },
    activity: {
      live: {
        main: '#ffffff', // White text for good contrast on green background
        background: '#22c55e',
      },
      active: {
        main: '#000000', // Black text for good contrast on amber background
        background: '#f59e0b',
      },
      new: {
        main: '#ffffff', // White text for good contrast on blue background
        background: '#2563eb',
      },
      inactive1: {
        main: '#ffffff', // White text for good contrast on dark grey background
        background: '#6b7280',
      },
      inactive2: {
        main: '#000000', // Black text for good contrast on medium grey background
        background: '#94a3b8',
      },
      inactive3: {
        main: '#000000', // Black text for good contrast on medium-light grey background
        background: '#a3a3a3',
      },
      inactive: {
        main: '#000000', // Black text for good contrast on light grey background
        background: '#d4d4d4',
      },
    },
    competition: {
      headout: {
        main: '#ffffff',
        background: '#8a1aff',
      },
      fever: {
        main: '#ffffff',
        background: '#ff6b6b',
      },
    },
    map: {
      userLocationActual: '#4285f4', // Google blue
      userLocationDefault: '#ff6b35', // Orange for default location
      userLocationPulseActual: 'rgba(66, 133, 244, 0.2)',
      userLocationPulseDefault: 'rgba(255, 107, 53, 0.2)',
      dealMarkerPulsation: 'rgba(255, 215, 0, 0.3)', // Gold pulsation
    },
    google: {
      border: '#dadce0',
      text: '#3c4043',
      background: '#ffffff',
      hoverBackground: '#f8f9fa',
      loadingColor: '#5f6368',
      disabledText: '#5f6368',
      blue: '#4285F4',
      green: '#34A853',
      yellow: '#FBBC05',
      red: '#EA4335',
    },
    salesforce: {
      blue: '#0176d3',
    },
    gradients: {
      coffeeLight: 'linear-gradient(135deg, #fef7ed 0%, #fed7aa 50%, #fdba74 100%)',
      coffeeDark: 'linear-gradient(135deg, #1c1917 0%, #292524 50%, #3c3c3c 100%)',
    },
  },
};

// Base theme configuration with comprehensive component styling
const baseTheme: ThemeOptions = {
  spacing: (factor: number) => `${factor * 8}px`,
  typography: {
    fontFamily: [
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      '"Oxygen"',
      '"Ubuntu"',
      '"Cantarell"',
      '"Open Sans"',
      '"Helvetica Neue"',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          padding: '10px 24px',
          boxShadow: 'none',
          minHeight: '44px', // Touch-friendly
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
          },
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: '1rem',
          minHeight: '48px',
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.875rem',
          minHeight: '36px',
        },
        contained: {
          '&:hover': {
            filter: 'brightness(0.95)',
          },
          '&:active': {
            filter: 'brightness(0.9)',
          },
        },
      },
      variants: [
        {
          props: { variant: 'pill' },
          style: ({ theme }) => ({
            borderRadius: '20px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: '1px solid',
            borderColor: theme.palette.divider,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            height: 40,
            minHeight: 40,
            maxHeight: 40,
            paddingLeft: 16,
            paddingRight: 16,
            '&:hover': {
              backgroundColor: theme.palette.background.paper,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            },
            '&:active': {
              backgroundColor: theme.palette.action.hover,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            },
            '&:focus': {
              backgroundColor: theme.palette.background.paper,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            },
          }),
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          borderRadius: 12,
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxSizing: 'border-box',
          position: 'relative',
          height: '100%',
          border: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          height: 'auto',
          minHeight: '32px',
        },
        sizeSmall: {
          height: '24px',
          minHeight: '24px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          minWidth: '44px',
          minHeight: '44px',
        },
        sizeSmall: {
          minWidth: '36px',
          minHeight: '36px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        elevation2: {
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)',
        },
        elevation3: {
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          borderRadius: '50%',
          minWidth: '20px',
          height: '20px',
          fontSize: '0.75rem',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiAlert-root': {
            borderRadius: 12,
          },
        },
      },
    },
  },
};

// Light theme
export const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    ...coffeeColors,
    background: {
      default: '#fafaf9',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937',
      secondary: '#6b7280',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
  },
});

// Dark theme
export const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#f59e0b', // Golden yellow for better contrast in dark mode
      light: '#f6a824',
      dark: '#d8890a',
      contrastText: '#000000',
    },
    secondary: coffeeColors.secondary,
    success: coffeeColors.success,
    warning: coffeeColors.warning,
    error: coffeeColors.error,
    background: {
      default: '#1a1a1a',
      paper: '#2d2d2d',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b5b5b5',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    // Custom app colors remain the same in dark mode (they have their own contrast)
    app: coffeeColors.app,
  },
});

// Helper function to get theme based on mode
export const getTheme = (isDark: boolean) => (isDark ? darkTheme : lightTheme);
